import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { createAdmin, updateAdmin, getAdmins } from '../../services/api';

function AdminForm({ admin }) {
  const { adminId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(adminId);
  const isSuperAdmin = admin.role === 'super_admin';
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirm_password: '',
    first_name: '',
    last_name: '',
    role: 'moderator',
    permissions: [],
    is_active: true
  });

  useEffect(() => {
    if (isEdit && isSuperAdmin) {
      fetchAdmin();
    }
  }, [adminId, isSuperAdmin]);

  const fetchAdmin = async () => {
    try {
      const response = await getAdmins();
      const adminUser = response.data.admins.find(a => a.admin_id == adminId);
      
      if (adminUser) {
        setFormData({
          email: adminUser.email || '',
          password: '',
          confirm_password: '',
          first_name: adminUser.first_name || '',
          last_name: adminUser.last_name || '',
          role: adminUser.role || 'moderator',
          permissions: adminUser.permissions ? JSON.parse(adminUser.permissions) : [],
          is_active: adminUser.is_active
        });
      }
    } catch (err) {
      setError('Failed to load admin details: ' + err.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate passwords
    if (!isEdit && formData.password !== formData.confirm_password) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (!isEdit && formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    try {
      const submitData = {
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        role: formData.role,
        permissions: formData.permissions,
        is_active: formData.is_active
      };

      // Only include password if provided (for edit) or required (for create)
      if (formData.password && (!isEdit || formData.password)) {
        submitData.password = formData.password;
      }

      const response = isEdit 
        ? await updateAdmin(adminId, submitData)
        : await createAdmin(submitData);

      if (response.success) {
        navigate('/admin/system/admins');
      }
    } catch (err) {
      setError(`Failed to ${isEdit ? 'update' : 'create'} admin: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const togglePermission = (permission) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  };

  if (!isSuperAdmin) {
    return (
      <div className="min-h-full bg-gray-50 p-6">
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">🚫</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
          <p className="text-gray-500 mb-4">Only super admins can manage admin users.</p>
          <Link
            to="/admin/system/admins"
            className="bg-[#ff660e] text-white px-6 py-2 rounded-lg hover:bg-[#e55b0c] transition-colors"
          >
            Back to Admin Users
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEdit ? 'Edit Admin User' : 'Create Admin User'}
            </h1>
            <p className="text-gray-600 mt-1">
              {isEdit ? 'Update admin user details and permissions' : 'Add a new system administrator'}
            </p>
          </div>
          <Link
            to="/admin/system/admins"
            className="text-gray-600 hover:text-gray-800 text-sm font-medium"
          >
            ← Back to Admin Users
          </Link>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <span className="text-red-500">⚠️</span>
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <form onSubmit={handleSubmit}>
            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
                      value={formData.first_name}
                      onChange={(e) => handleChange('first_name', e.target.value)}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
                      value={formData.last_name}
                      onChange={(e) => handleChange('last_name', e.target.value)}
                      required
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Password */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {isEdit ? 'Change Password' : 'Password'}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {isEdit ? 'New Password' : 'Password *'}
                    </label>
                    <input
                      type="password"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
                      value={formData.password}
                      onChange={(e) => handleChange('password', e.target.value)}
                      required={!isEdit}
                      placeholder={isEdit ? 'Leave blank to keep current password' : ''}
                    />
                  </div>

                  {!isEdit && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Confirm Password *
                      </label>
                      <input
                        type="password"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
                        value={formData.confirm_password}
                        onChange={(e) => handleChange('confirm_password', e.target.value)}
                        required
                      />
                    </div>
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Password must be at least 8 characters long
                </p>
              </div>

              {/* Role & Permissions */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Role & Permissions</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Role *
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
                      value={formData.role}
                      onChange={(e) => handleChange('role', e.target.value)}
                      required
                    >
                      <option value="moderator">Moderator</option>
                      <option value="admin">Admin</option>
                      <option value="super_admin">Super Admin</option>
                    </select>
                    <div className="mt-2 text-sm text-gray-500">
                      {formData.role === 'moderator' && 'Can manage content and users'}
                      {formData.role === 'admin' && 'Full access except system settings'}
                      {formData.role === 'super_admin' && 'Full system access including settings'}
                    </div>
                  </div>

                  <div className="flex items-end">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-[#ff660e] focus:ring-[#ff660e] border-gray-300 rounded"
                        checked={formData.is_active}
                        onChange={(e) => handleChange('is_active', e.target.checked)}
                      />
                      <span className="text-sm text-gray-700">Active Account</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Additional Permissions */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Permissions</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    'manage_users',
                    'manage_listings', 
                    'manage_categories',
                    'manage_orders',
                    'view_reports',
                    'manage_payments'
                  ].map(permission => (
                    <label key={permission} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-[#ff660e] focus:ring-[#ff660e] border-gray-300 rounded"
                        checked={formData.permissions.includes(permission)}
                        onChange={() => togglePermission(permission)}
                      />
                      <span className="text-sm text-gray-700 capitalize">
                        {permission.replace('_', ' ')}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex justify-end space-x-3">
                <Link
                  to="/admin/system/admins"
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </Link>
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-[#ff660e] text-white px-6 py-2 rounded-lg hover:bg-[#e55b0c] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? 'Saving...' : (isEdit ? 'Update Admin' : 'Create Admin')}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AdminForm;